import { createSlice } from "@reduxjs/toolkit";
import {
    updateDisabledFields,
    updatePatternSideExceptions,
    updatePatternSideModuleExceptions
} from "../exceptions/disabledFieldsSlice";
import {generateCatalogNumber, managePatternSummary} from "../configuratorStateSlice";
import {
    quoteItemModuleAttributeSelectionsSet,
    setSideAttributeSelections,
    setSideModules,
    sideModuleSelectionsSet
} from "./productDataSlice";
import patternApi from "../../configurator/services/patternApi";
import {getPatternSummary} from "../summarySlice";

/*
sides: [
    sideKey: {
        requiredAttributes: [],
        optionalAttributes: [],
        perModuleAttributes: [],
        requiredSelections: [],
        optionalSelections: [],
        perModuleSelections: []
    }
]

 */

export const patternSelectionsSlice =  createSlice({
    name: 'patternSelections',
    initialState: {
        selectionsLoading: 'idle',
        sides: [],
    },
    reducers: {
        patternSelectionsLoading(state ,action){
            if(state.selectionsLoading == 'idle'){
                state.selectionsLoading = 'pending'
            }
        },
        patternSidesSet(state, action) {
            if(state.selectionsLoading == 'pending'){
                state.selectionsLoading = 'idle';
                state.sides = action.payload;
            }
        },

    }
});
export const { patternSelectionsLoading, patternSidesSet } = patternSelectionsSlice.actions;

export const setPatternSides = (sides) => dispatch => {
    dispatch(patternSelectionsLoading())
    dispatch(patternSidesSet(sides))
}

export const separatePatternAttributes = (patternAttributes) => {
    const required = patternAttributes.filter(pa => !pa.optional && !pa.per_module);
    const optional = patternAttributes.filter(pa => pa.optional && !pa.per_module);
    const requiredSel = patternAttributes.filter(pa => !pa.optional && !pa.per_module && pa.selection)
    const optionalSel = patternAttributes.filter(pa => pa.optional && !pa.per_module && pa.selection)
    const perModule = patternAttributes.filter(pa => pa.optional && pa.per_module && !pa.read_only);
    const perModuleSel = patternAttributes.filter(pa => pa.optional && pa.per_module && pa.selection);
    const data = {
        sideRequiredAttributes: required,
        sideOptionalAttributes: optional,
        sidePerModuleAttributes: perModule,
        sideRequiredSelections: requiredSel,
        sideOptionalSelections: optionalSel,
        sidePerModuleSelections: perModuleSel
    };
    return data;
}

export const separatePatternAttributesFromLoad = (patternAttributes, patternSelections, sideModuleSelections) => {
    const required = patternAttributes.filter(pa => !pa.optional && !pa.per_module);
    const optional = patternAttributes.filter(pa => pa.optional && !pa.per_module);
    const requiredSel = patternSelections.filter(pa => !pa.optional && !pa.per_module && pa.selection)
    const optionalSel = patternSelections.filter(pa => pa.optional && !pa.per_module && pa.selection)
    const perModule = patternAttributes.filter(pa => pa.optional && pa.per_module && !pa.read_only);
    const data = {
        sideRequiredAttributes: required,
        sideOptionalAttributes: optional,
        sidePerModuleAttributes: perModule,
        sideRequiredSelections: requiredSel,
        sideOptionalSelections: optionalSel,
        sidePerModuleSelections: sideModuleSelections
    };
    return data;
}

// This will split side attributes into our sides array in the reducer
export const normalizeSides = () => (dispatch, getState) => {
    dispatch(patternSelectionsLoading())
    const state = getState();
    const data = state.productDataReducer.side_attributes;
    const sides = [];
    for( const [sideKey, side] of Object.entries(data)) {
        const sideData = separatePatternAttributes(side.product_attributes);
        const s = { [sideKey]: sideData };
        sides.push(s);
    }

    dispatch(setPatternSides(sides))
}

// Handles changes to a selection and updates attributeSelections
export const patternSideSelectionChanged = (field, selection, side) => (dispatch, getState) => {
    dispatch(patternSelectionsLoading());
    const state = getState();
    const sides = Object.assign([], state.patternSelectionsReducer.sides);
    const matchingSide = sides.find(si => Object.keys(si)[0].toString() == side )[side];
    const shouldUpdateModules = field.internal_name == 'length';
    let existingSelections;
    let existingAttributes;
    const matchingSides = [];
    // check if there are sides that match the same starting letter
    const hasMatchingSides = sides.filter(si => Object.keys(si)[0].toString()[0] == side[0] && Object.keys(si)[0] != 'CORNER').length > 1
    let isRequiredAttribute = false

    // depending on field type grab the existing store values
    if(!field.per_module) {
        if (field.optional) {
            existingAttributes = matchingSide.sideOptionalAttributes;
            existingSelections = matchingSide.sideOptionalSelections;
        } else {
            existingAttributes = matchingSide.sideRequiredAttributes;
            existingSelections = matchingSide.sideRequiredSelections;
            isRequiredAttribute = true;
        }
    }

    const updatedSelections = [];
    let updatedSide;

    // we assume here that a matching product selection is already set
    // we do an if to avoid any errors that may occur.
    existingAttributes.forEach((attribute) => {
        let productSelection = attribute;
        const matchingSel = existingSelections.find(sel => sel.id == attribute.id);
        if(matchingSel){
            productSelection = matchingSel;
        }

        if(attribute.id == field.id){
            productSelection = Object.assign({}, attribute, {
                selection: selection
            })
        }
        updatedSelections.push(productSelection);
    });

    if(!field.per_module) {
        if (field.optional) {
            updatedSide = Object.assign({},matchingSide, {sideOptionalSelections: updatedSelections});
        } else {
            updatedSide = Object.assign({}, matchingSide, {sideRequiredSelections: updatedSelections});
        }
    }
    const updatedSides = []
    sides.forEach(si => {
        if(Object.keys(si)[0].toString() == side) {
            updatedSides.push({ [side]: updatedSide})
            matchingSides.push(side);
        }  else {
            // THIS HANDLES MATCHING SIDE CHANGES FOR SQUARE AND RECTANGLE PATTERNS
            if(hasMatchingSides && isRequiredAttribute && Object.keys(si)[0][0] == side[0]){
             // update this sides required selection to match
                const currentSideKey = Object.keys(si)[0];
                const currentSideSelections = Object.assign([], si[currentSideKey].sideRequiredAttributes);
                const updatedSideSelections = []
                currentSideSelections.forEach(sideSel => {
                    if(sideSel.id == field.id){
                        updatedSideSelections.push(Object.assign({}, sideSel, { selection: selection}))
                    } else {
                        updatedSideSelections.push(sideSel);
                    }
                })
                const updatedSideValues = Object.assign({}, si[currentSideKey],{ sideRequiredSelections: updatedSideSelections})
                const updatedSide = { [currentSideKey]: updatedSideValues}
                updatedSides.push(updatedSide)
                matchingSides.push(currentSideKey);
                // END MATCHING SIDE LOGIC
            } else {
                updatedSides.push(si)
            }
        }
    })
    dispatch(setPatternSides(updatedSides));
    dispatch(setSideAttributeSelectionsFromSelections());

    if( shouldUpdateModules){
        matchingSides.forEach((s) => {
            dispatch(getPatternModules(s));
        })
    }

}

/**
 * @description Regenerates pattern side modules for all sides.
 */
export const getAllPatternModules = () => (dispatch, getState) => {
    const state = getState();
    const sides = Object.assign([], state.patternSelectionsReducer.sides);
    sides.forEach(s => {
        const side = Object.keys(s)[0];
        dispatch(getPatternModules(side));
    })
}

export const setSideAttributeSelectionsFromSelections = () => (dispatch, getState) => {
    const state = getState();
    const sides = state.patternSelectionsReducer.sides;
    let mergedSides = {};
    let mergedModules = {};
    let allModules = [];
    sides.forEach((side) => {
        const sideKey = Object.keys(side)[0];

        // modules require module_id removed,selections renamed to quote_item_module_attribute_selections,
        // and side to be added. we do that here before adding to our hash.
        const updatedModules = [];
        let perModuleSelectionsCount = [];
        side[sideKey].sidePerModuleSelections.forEach((mod, index) => {
            const selections = [];
            mod.quote_item_module_options.forEach(option => {
                const selection = mod.selections.includes(option.module_catalog_key)  ? option.module_catalog_key : ""
                if(selection) {
                    // here we tally up module selections per side, and add them to mergedSelections
                    perModuleSelectionsCount[option.module_catalog_key] = perModuleSelectionsCount[option.module_catalog_key] ? perModuleSelectionsCount[option.module_catalog_key] + 1: 1;

                    const opt = Object.assign({}, {
                        product_attribute_id: option.id,
                        selection: selection
                    })
                    selections.push(opt);
                }
            })

            updatedModules.push(Object.assign({}, {
                length: mod.length,
                position: mod.position,
                order: ++index,
                quote_item_module_attribute_selections_attributes: selections
            }))
        })

        const perModuleSelections = [];
        side[sideKey].sidePerModuleAttributes.forEach(attr => {
            const count = perModuleSelectionsCount[attr.module_catalog_key];
            const sel = count ? attr.module_catalog_key + count : null;
            if( sel) {
                perModuleSelections.push(Object.assign({}, attr, {
                    selection: sel
                }))
            }
        })

        const mergedSelections = side[sideKey].sideRequiredSelections.concat(side[sideKey].sideOptionalSelections.filter(opt => opt.selection)).concat(perModuleSelections);
        mergedSides = Object.assign({}, mergedSides, {
            [sideKey]: {
                product_attributes: mergedSelections
            }
        })

        allModules = allModules.concat(updatedModules);
        mergedModules = Object.assign({}, mergedModules, {
            [sideKey]: {
                quote_item_module_with_selections: updatedModules
            }
        })
    })
    dispatch(sideModuleSelectionsSet(mergedModules));
    dispatch(quoteItemModuleAttributeSelectionsSet(allModules));
    dispatch(setSideAttributeSelections(mergedSides));
    dispatch(managePatternSummary());
    dispatch(updateDisabledFields());
    dispatch(updatePatternSideExceptions());

}

export const setPatternSidesFromEdit = (product) => (dispatch) => {
    const sides = [];
    let sideModules = {};
    const sideAttributes = product.side_attributes;
    const sideSelections = product.side_attribute_selections;
    const sideModuleSelections = product.side_module_selections;

    // module selections are handled here first
    for (const [sideKey, modules] of Object.entries(sideModuleSelections)) {
        let moduleCount = 1;
        // we rely on quote_item_module_options to update selections, since our api request doesnt include
        // module_catalog_key, and only has selected options, we gather our options and inject it below.
        const perModuleAttributes = sideAttributes[sideKey].product_attributes.filter(pa => pa.per_module && pa.optional)
        const modulesWithOptions = modules.map(mod => {
            const selections = mod.quote_item_module_attribute_selections.map(m => m.selection)
            return Object.assign({}, mod, {
                quote_item_module_options: perModuleAttributes,
                selections: selections,
                module_id: moduleCount++,
                // modules are all separate so quantity is always one
                quantity: 1
            })
        })

        sideModules = Object.assign({}, sideModules, {
            [sideKey]: modulesWithOptions
        })
    }

    // this handles required and optional selections
    for (const [sideKey, side] of Object.entries(sideAttributes)) {
        const sideData = separatePatternAttributesFromLoad(side.product_attributes, sideSelections[sideKey].product_attributes, sideModules[sideKey]);
        const s = { [sideKey]: sideData };
        sides.push(s);
    }
    dispatch(setPatternSides(sides));

}

export const getPatternModules = (side) => async(dispatch, getState) => {
    const state = getState();
    const productData = state.productDataReducer;
    const patternId = state.patternDetailsReducer.patternId;
    const patternCode = state.patternDetailsReducer.patternCode;
    const mergedData = Object.assign({}, productData, {
        pattern_code: patternCode
    });
    const data = {
        product: mergedData,
        side_selected: side
    }
    const response = await patternApi.getPatternModulesFromSelections(data, patternId);
    const modules = []
    let module_id = 1;

    if(response && response.leg_module_catalog_numbers) {
        response.leg_module_catalog_numbers.forEach((module) => {
            // this loop will duplicate middle modules that have more than one quantity
            // we do this to remove the need for select box deciding how many options to assign per module,
            // and can use checkboxes instead to toggle a feature.
            for (let i = 0; i < module.quantity; i++) {
                // add module_id to our module
                const m = Object.assign({}, module, {
                    module_id: module_id
                })
                modules.push(m)
                // increment module id after adding it
                module_id++
            }
        })
    }

        dispatch(setSideModules(side, modules))
        dispatch(updatePatternSideModuleExceptions());
        dispatch(setSideAttributeSelectionsFromSelections())

}

export const setSideModuleSelections = (sideKey, selections) => (dispatch, getState)=> {
    const state = getState();
    const sides = state.patternSelectionsReducer.sides;
    const updatedSides = [];
    sides.forEach(side => {
        const side_key = Object.keys(side)[0];
        if(side_key == sideKey){
            const updatedSideContent = Object.assign({}, side[sideKey], {
                sidePerModuleSelections: selections
            })

            updatedSides.push({[sideKey]: updatedSideContent });
        } else {
            updatedSides.push(side)
        }
    })
    dispatch(setPatternSides(updatedSides))
}

export const sidePerModuleSelectionChanged = (side, module_id, value, module_key, attribute_data) => (dispatch, getState) => {
    const state = getState();
    const sides = state.patternSelectionsReducer.sides;
    const matchingSide = sides.find(s => Object.keys(s)[0] == side);
    if(!matchingSide){
        // prevent errors
        return;
    }
    const currentSelections = matchingSide[side].sidePerModuleSelections;
    const updatedSelections = [];
    // go through each selection and compare module id.
    // matching module_id means we need to update the selection
    currentSelections.forEach(selection => {
        let sel = selection;
        let selections = Object.assign([], selection.selections);
        if(selections.includes(module_key)){
            // containing module_key means it was previously selected, so deselect
            selections = selections.filter(sels => sels != module_key);
        } else {
            // otherwise add to selection
            selections.push(value);
        }
        selections = selections.filter(sel => sel);
        if(sel.module_id == module_id){
            sel = Object.assign({}, selection, {
                selections: selections
            })
        }
        updatedSelections.push(sel);
    })

    dispatch(setSideModuleSelections(side, updatedSelections));
    if(attribute_data.pattern_linked_attribute_id){
        dispatch(toggleLinkedAttributes(attribute_data.pattern_linked_attribute_id, module_key));
    }

    dispatch(setSideAttributeSelectionsFromSelections())
    // dispatch(updateDisabledFields());
    // here we generate catalog number and refire the summary api call
    // since we know summary will be displaying if a module changes, we call
    // get summary directly instead of through the configuratorStateReducer.
    dispatch(generateCatalogNumber())
    dispatch(getPatternSummary())
}

export const toggleLinkedAttributes = (attributeId, moduleKey) => (dispatch, getState) => {
    // This function toggles linked attributes for all sides that does not have module key in its selections
     const state = getState();
     const sides = state.patternSelectionsReducer.sides;
     const updatedSides = [];
    // first we need to check if there are any toggle values as a selection
    // flatMap returns a single array instead of multi, so we can check if
    // it contains our value;
    const selectedModuleOptions = sides.flatMap(side => {
        const key = Object.keys(side)[0]
        return side[key].sidePerModuleSelections.flatMap(sel => sel.selections)
    });
    const toggled = selectedModuleOptions.includes(moduleKey)

    sides.forEach(side => {
         const sideKey = Object.keys(side)[0];
         const currentSide = side[sideKey];
         const sideOptional = currentSide.sideOptionalAttributes.find(opt => opt.id == attributeId);
         const sideSelection = currentSide.sideOptionalSelections.find(opt => opt.id == attributeId);
         const perModuleSelections = currentSide.sidePerModuleSelections.flatMap(sel => sel.selections);
         // sides only toggle if there isnt the matching key
         const sideToggled = !perModuleSelections.includes(moduleKey);
         const toggledValue = sideOptional.values.split(",")[0];
         // check sideSelections for the toggled selection
         const value = (toggled && sideToggled) ? toggledValue : '';
         const field = sideSelection ? sideSelection : sideOptional

         const updatedField = Object.assign({}, field, {
             selection: value
         });

         const updatedSideOptionalSelections = [];
         // if there are no selections we push the updated field
        // otherwise go through and update any selections
         if(!currentSide.sideOptionalSelections.find(sel => sel.id == attributeId ) && value){
             updatedSideOptionalSelections.push(updatedField)
         } else {
             currentSide.sideOptionalSelections.forEach(attr => {
                 // if the value matches, update it, otherwise leave it alone
                 if (attr.id == attributeId) {
                     if( value) {
                         // if there is no value, then we dont add the fields as
                         // summary will through an error for empty selection.
                         updatedSideOptionalSelections.push(updatedField);
                     }
                 } else {
                     updatedSideOptionalSelections.push(attr);
                 }
             })
         }

         const updatedSide = Object.assign({}, currentSide, {
                 sideOptionalSelections: updatedSideOptionalSelections
         })
         // check all module selections for value
         // const shouldBeActive =
         updatedSides.push({[sideKey]: updatedSide});
     })

     dispatch(setPatternSides(updatedSides));
     // this function only runs when module selections are changed, setSideAttributeSelectionsFromSelections
     // is called after by sidePerModuleSelectionChanged
}

/**
 * @description Checks if all sides contain the length attribute with a non empty selection
 * @param sides Current side data
 * @returns {boolean} true if all sides have length attribute with selection, otherwise false
 */
export const allSidesHaveLength = (sides) => {
    let sidesHaveLength = true;
    // side selections are stored in a hash so we need to get the key to get them
    const sideRequiredSelections = sides.map(si => si[Object.keys(si)[0]].sideRequiredSelections)
    sideRequiredSelections.forEach(selection => {
        const len = selection.find(sel => sel.internal_name === 'length' && selection);
        if(!len){
            sidesHaveLength = false;
        }
    })
    return sidesHaveLength;
}

const reducer = patternSelectionsSlice.reducer;
export default reducer
